import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { isValidEmail } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { AuthShell } from "./Login";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

const Register = () => {
  const { t } = useT();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || undefined;
  const emailFromUrl = searchParams.get("email") || "";
  const [form, setForm] = useState({
    name: "",
    email: emailFromUrl,
    password: "",
    role: "huurder",
    agency_name: "",
    kvk_number: "",
    nvm_vbo_number: "",
    terms: false,
    marketing_consent: true,
  });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Vul je naam in.");
    if (!isValidEmail(form.email)) return setError(t("auth.err.email"));
    if (form.password.length < 6) return setError(t("auth.err.password"));
    if (!form.terms) return setError(t("auth.register.terms"));
    if (form.role === "makelaar") {
      if (!form.agency_name.trim()) return setError("Vul de naam van je makelaarskantoor in.");
      if (!/^\d{8}$/.test(form.kvk_number.trim())) return setError("Vul een geldig 8-cijferig KvK-nummer in.");
    }
    setBusy(true);
    try {
      await register(form.email, form.password, form.name, {
        role: form.role,
        agency_name: form.agency_name,
        kvk_number: form.kvk_number,
        nvm_vbo_number: form.nvm_vbo_number,
        terms_accepted: true,
        marketing_consent: form.marketing_consent,
        ref_code: refCode,
      });
      navigate(form.role === "makelaar" ? "/makelaar/wacht-op-goedkeuring" : "/welkom");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("auth.register.title")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-sm font-semibold text-navy">{t("common.name")}</Label>
          <Input className="mt-2 rounded-xl" data-testid="register-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <Label className="text-sm font-semibold text-navy">{t("common.email")}</Label>
          <Input className="mt-2 rounded-xl" data-testid="register-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
        </div>
        <div>
          <Label className="text-sm font-semibold text-navy">{t("common.password")}</Label>
          <div className="relative mt-2">
            <Input
              className="rounded-xl pr-10"
              data-testid="register-password"
              type={show ? "text" : "password"}
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              autoComplete="new-password"
            />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-navy">{t("auth.register.role")}</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[
              { v: "huurder", l: t("auth.register.huurder") },
              { v: "makelaar", l: t("auth.register.makelaar") },
            ].map((r) => (
              <button
                key={r.v}
                type="button"
                data-testid={`register-role-${r.v}`}
                onClick={() => set("role", r.v)}
                className={`h-11 rounded-xl border text-sm font-semibold transition-colors ${
                  form.role === r.v ? "border-brandblue bg-brandblue-light text-brandblue" : "border-border text-slate-600"
                }`}
              >
                {r.l}
              </button>
            ))}
          </div>
        </div>
        {form.role === "makelaar" && (
          <div className="space-y-3 rounded-2xl border border-brandblue-light/60 bg-brandblue-light/30 p-4" data-testid="makelaar-fields">
            <p className="text-xs font-medium text-brandblue">
              Je account wordt handmatig geverifieerd. Vul je bedrijfsgegevens in zodat we je kunnen goedkeuren.
            </p>
            <div>
              <Label className="text-sm font-semibold text-navy">Naam makelaarskantoor</Label>
              <Input
                className="mt-2 rounded-xl"
                data-testid="register-agency-name"
                value={form.agency_name}
                onChange={(e) => set("agency_name", e.target.value)}
                placeholder="bv. De Wijk Makelaars"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-navy">KvK-nummer</Label>
              <Input
                className="mt-2 rounded-xl"
                data-testid="register-kvk"
                value={form.kvk_number}
                onChange={(e) => set("kvk_number", e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="8 cijfers"
                inputMode="numeric"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-navy">NVM / VBO / VastgoedPRO nummer <span className="font-normal text-slate-400">(optioneel)</span></Label>
              <Input
                className="mt-2 rounded-xl"
                data-testid="register-nvm"
                value={form.nvm_vbo_number}
                onChange={(e) => set("nvm_vbo_number", e.target.value)}
                placeholder="Lidmaatschapsnummer"
              />
            </div>
          </div>
        )}
        <label className="flex items-start gap-2.5 text-sm text-slate-600">
          <Checkbox data-testid="register-terms" checked={form.terms} onCheckedChange={(v) => set("terms", !!v)} className="mt-0.5" />
          <span>{t("auth.register.terms")}</span>
        </label>
        <label className="flex items-start gap-2.5 text-sm text-slate-600" data-testid="register-consent-label">
          <Checkbox
            data-testid="register-marketing-consent"
            checked={form.marketing_consent}
            onCheckedChange={(v) => set("marketing_consent", !!v)}
            className="mt-0.5"
          />
          <span>
            Stuur mij tips, updates en woningnieuws per e-mail. Je kunt je met één klik uitschrijven.
          </span>
        </label>
        {error && <p className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="register-submit" className="w-full rounded-xl bg-brandblue hover:bg-brandblue/90">
          {busy ? <Loader2 className="animate-spin" size={18} /> : t("auth.register.submit")}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        {t("auth.register.hasAccount")}{" "}
        <Link to="/login" className="font-semibold text-brandblue">
          {t("nav.login")}
        </Link>
      </p>
    </AuthShell>
  );
};

export default Register;
